/**
 * Vue Router Configuration - 路由設定
 * 
 * Defines application routes and navigation guards for the
 * Kingde Customs Broker System.
 * 
 * Routes:
 * - /login: Login page (public)
 * - /: Redirects to /declarations
 * - /declarations: Declaration list (requires auth)
 * - /declarations/:declNo: Declaration detail (requires auth)
 * - /archives: Archived declarations (requires auth)
 * - /customers: Customer management (requires admin)
 * - /users: User management (requires admin)
 * - /quotation: Quotation query (requires auth)
 * - /equipments/dashboard: Equipment management (requires auth)
 * 
 * Navigation Guards:
 * - Unauthenticated users are redirected to login page
 * - Non-admin users are redirected to home when accessing admin routes
 * - Stores intended destination for redirect after login
 * 
 * Requirements: 11.1, 12.1, 12.5, 12.7
 */

import { createRouter, createWebHistory } from 'vue-router';
import type { RouteRecordRaw, NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import {
  evaluateNavigationGuard,
  requiresAuthentication,
  requiresAdminPermission,
  storeRedirectPath,
  getAndClearRedirectPath,
  REDIRECT_KEY,
} from './guards';

// Re-export guard functions for backward compatibility
export {
  evaluateNavigationGuard,
  requiresAuthentication,
  requiresAdminPermission,
  storeRedirectPath,
  getAndClearRedirectPath,
  REDIRECT_KEY,
};

// Augment Vue Router's RouteMeta interface
declare module 'vue-router' {
  interface RouteMeta {
    /** Whether the route requires authentication */
    requiresAuth?: boolean;
    /** Whether the route requires admin permission */
    requiresAdmin?: boolean;
    /** Page title in Traditional Chinese */
    title?: string;
  }
}

// Lazy-loaded view components
const LoginView = () => import('@/views/LoginView.vue');
const DeclarationListView = () => import('@/views/DeclarationListView.vue');
const DeclarationDetailView = () => import('@/views/DeclarationDetailView.vue');
const ArchiveListView = () => import('@/views/ArchiveListView.vue');
const UserManagementView = () => import('@/views/UserManagementView.vue');
const QuotationView = () => import('@/views/QuotationView.vue');
const EquipmentDashboard = () => import('@/views/EquipmentDashboard.vue');
const OrderListView = () => import('@/views/OrderListView.vue');
const OrderArchiveListView = () => import('@/views/OrderArchiveListView.vue');
const ImportDeclarationView = () => import('@/views/ImportDeclarationView.vue');
const CustomerManagementView = () => import('@/views/CustomerManagementView.vue');

/**
 * Application routes
 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: {
      requiresAuth: false,
      title: '登入',
    },
  },
  {
    path: '/',
    name: 'Home',
    redirect: '/declarations',
  },
  {
    path: '/declarations',
    name: 'DeclarationList',
    component: DeclarationListView,
    meta: {
      requiresAuth: true,
      title: '報單清單',
    },
  },
  {
    path: '/declarations/:declNo',
    name: 'DeclarationDetail',
    component: DeclarationDetailView,
    meta: {
      requiresAuth: true,
      title: '報單詳情',
    },
  },
  {
    path: '/archives',
    name: 'ArchiveList',
    component: ArchiveListView,
    meta: {
      requiresAuth: true,
      title: '封存報單',
    },
  },
  {
    path: '/orders',
    name: 'OrderList',
    component: OrderListView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: '訂單清單',
    },
  },
  {
    path: '/orders/archives',
    name: 'OrderArchiveList',
    component: OrderArchiveListView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: '封存訂單',
    },
  },
  {
    path: '/orders/:orderKey/declaration',
    name: 'ImportDeclaration',
    component: ImportDeclarationView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: '進口報單',
    },
  },
  {
    path: '/customers',
    name: 'CustomerManagement',
    component: CustomerManagementView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: '客戶管理',
    },
  },
  {
    path: '/users',
    name: 'UserManagement',
    component: UserManagementView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: '使用者管理',
    },
  },
  {
    path: '/quotation',
    name: 'Quotation',
    component: QuotationView,
    meta: {
      requiresAuth: true,
      requiresAdmin: true, // 2026-10-03: quotation system is admin-only end to end (API enforces too)
      title: '報價查詢',
    },
  },
  {
    path: '/equipments/dashboard',
    name: 'EquipmentDashboard',
    component: EquipmentDashboard,
    meta: {
      requiresAuth: true,
      requiresAdmin: true, // 2026-10-03: quotation system is admin-only end to end (API enforces too)
      title: '配件管理',
    },
  },
  // Catch-all route for 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    redirect: '/declarations',
  },
];

/**
 * Create the router instance
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

/**
 * Global navigation guard
 * 
 * Checks authentication and permission before each navigation.
 * Redirects unauthenticated users to login page.
 * Redirects non-admin users away from admin routes.
 * 
 * Requirements: 12.7
 */
router.beforeEach(async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore();

  // Initialize auth store if not already done
  if (!authStore.isInitialized) {
    await authStore.initialize();
  }

  // Evaluate the navigation guard
  const result = evaluateNavigationGuard(
    to,
    authStore.isAuthenticated,
    authStore.isAdmin
  );

  if (typeof result === 'string') {
    // Redirect to the specified path
    next(result);
  } else {
    // Proceed with navigation
    next();
  }
});

/**
 * Update document title after each navigation
 */
router.afterEach((to: RouteLocationNormalized) => {
  const title = to.meta.title || '錦德報關行系統';
  document.title = `${title} | 錦德報關行`;
});

export default router;
